/**
 * WidgetPage — Standalone Sophie chat UI served at /widget/:token.
 *
 * Loaded in an <iframe> by the embed script. Completely isolated from
 * the host site's CSS. No admin chrome, no auth.
 *
 * Architecture:
 * - History is kept in localStorage (keyed by token + session_id).
 * - On each send, the full history is sent to POST /api/public/{token}/chat.
 * - The backend runs Sophie with the customer-facing prompt + restricted tools.
 * - scheduling.py remains the single source of truth — zero availability
 *   logic lives here.
 */

import { useState, useEffect, useRef, useCallback } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { getBackendUrl } from "../lib/config";

const pub = axios.create();
pub.interceptors.request.use(async (config) => {
  config.baseURL = `${await getBackendUrl()}/api`;
  return config;
});

// ── Session persistence ──────────────────────────────────────────────────────
function getSessionKey(token) {
  return `sophie_widget_${token}`;
}

function loadSession(token) {
  try {
    const raw = localStorage.getItem(getSessionKey(token));
    if (!raw) return null;
    const obj = JSON.parse(raw);
    // Expire sessions older than 24h
    if (obj.createdAt && Date.now() - obj.createdAt > 86_400_000) return null;
    return obj;
  } catch {
    return null;
  }
}

function saveSession(token, session) {
  try {
    localStorage.setItem(getSessionKey(token), JSON.stringify(session));
  } catch {
    // storage full — ignore
  }
}

// ── Helpers ──────────────────────────────────────────────────────────────────
function newSessionId() {
  return crypto.randomUUID
    ? crypto.randomUUID()
    : Math.random().toString(36).slice(2) + Date.now().toString(36);
}

// Parse Sophie's plain text response (preserve newlines, no XSS)
function renderText(text) {
  if (!text) return null;
  return text.split("\n").map((line, i) => (
    <span key={i}>
      {line}
      {i < text.split("\n").length - 1 && <br />}
    </span>
  ));
}

// ── Typing indicator ─────────────────────────────────────────────────────────
function TypingDots() {
  return (
    <div className="w-flex flex items-center gap-0.5 px-4 py-3" data-testid="typing-indicator">
      {[0, 1, 2].map((i) => (
        <span
          key={i}
          className="w-1.5 h-1.5 rounded-full bg-[#9DB5A6] animate-bounce"
          style={{ animationDelay: `${i * 0.15}s` }}
        />
      ))}
    </div>
  );
}

// ── Message bubble ───────────────────────────────────────────────────────────
// Phase 10H — uses the `--widget-accent` CSS variable (set on the page-level
// container from ctx.widget_accent_color). Falls back to #1A3626 if unset.
function Bubble({ role, content }) {
  const isUser = role === "user";
  if (role === "tool") return null; // never shown to customers

  return (
    <div className={`flex ${isUser ? "justify-end" : "justify-start"} mb-3 px-3`}>
      {!isUser && (
        <div
          className="w-7 h-7 rounded-full flex-shrink-0 flex items-center justify-center text-white text-xs font-semibold mr-2 mt-1 select-none"
          style={{ backgroundColor: "var(--widget-accent, #1A3626)" }}
        >
          S
        </div>
      )}
      <div
        className={`max-w-[78%] rounded-2xl px-4 py-2.5 text-sm leading-relaxed ${
          isUser
            ? "text-white rounded-br-md"
            : "bg-white border border-[#E8E3DC] text-[#1C201E] rounded-bl-md shadow-sm"
        }`}
        style={isUser ? { backgroundColor: "var(--widget-accent, #1A3626)" } : undefined}
        data-testid={isUser ? "user-bubble" : "sophie-bubble"}
      >
        {renderText(content)}
      </div>
    </div>
  );
}

// ── Main widget component ────────────────────────────────────────────────────
export default function WidgetPage() {
  const { token } = useParams();

  const [ctx, setCtx] = useState(null);
  const [pageError, setPageError] = useState(null);
  const [ctxLoading, setCtxLoading] = useState(true);

  // Display messages (includes welcome + all exchanges)
  const [messages, setMessages] = useState([]);
  // API history = messages minus the welcome message
  const [apiHistory, setApiHistory] = useState([]);
  const [sessionId, setSessionId] = useState(null);

  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [inputFocused, setInputFocused] = useState(false);

  const bottomRef = useRef(null);
  const inputRef = useRef(null);

  // ── Load context + session on mount ───────────────────────────────────────
  useEffect(() => {
    (async () => {
      try {
        const { data } = await pub.get(`/public/${token}/widget-context`);
        setCtx(data);

        // Restore or create session
        const saved = loadSession(token);
        if (saved && saved.messages?.length > 0) {
          setMessages(saved.messages);
          setApiHistory(saved.apiHistory || []);
          setSessionId(saved.sessionId);
        } else {
          const sid = newSessionId();
          setSessionId(sid);
          // Welcome message (display only — not sent to API).
          // Phase 10H — salon-configurable greeting; falls back to the
          // default if blank (validated server-side: ≤200 chars, plain text).
          const greeting = (data.widget_greeting && data.widget_greeting.trim()) ||
            `Hi! I'm Sophie, your booking assistant at ${data.salon_name}. I can help you book an appointment, check availability, or answer any questions. How can I help?`;
          const welcome = {
            id: "welcome",
            role: "assistant",
            content: greeting,
            isWelcome: true,
          };
          setMessages([welcome]);
          setApiHistory([]);
          saveSession(token, { sessionId: sid, messages: [welcome], apiHistory: [], createdAt: Date.now() });
        }
      } catch (e) {
        const status = e.response?.status;
        if (status === 404) setPageError("This widget link is no longer valid.");
        else if (status === 403) setPageError("The booking widget is not currently available.");
        else setPageError("Unable to load the booking assistant. Please try again.");
      } finally {
        setCtxLoading(false);
      }
    })();
  }, [token]);

  // ── Auto-scroll ────────────────────────────────────────────────────────────
  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, loading]);

  // ── Send message ───────────────────────────────────────────────────────────
  const sendMessage = useCallback(async () => {
    const text = input.trim();
    if (!text || loading) return;
    setInput("");

    const userMsg = { id: Date.now().toString(), role: "user", content: text };
    const nextDisplay = [...messages, userMsg];
    const nextHistory = [...apiHistory, { role: "user", content: text }];

    setMessages(nextDisplay);
    setApiHistory(nextHistory);
    setLoading(true);

    try {
      const { data } = await pub.post(`/public/${token}/chat`, {
        messages: nextHistory,
        session_id: sessionId,
      });

      // Append Sophie's responses (skip tool messages for display)
      const newDisplayMsgs = data.messages
        .filter((m) => m.role !== "tool")
        .map((m, i) => ({ id: `r-${Date.now()}-${i}`, ...m }));

      const finalDisplay = [...nextDisplay, ...newDisplayMsgs];
      const finalHistory = [...nextHistory, ...data.messages];

      setMessages(finalDisplay);
      setApiHistory(finalHistory);
      saveSession(token, {
        sessionId,
        messages: finalDisplay,
        apiHistory: finalHistory,
        createdAt: Date.now(),
      });
    } catch (e) {
      const detail = e.response?.data?.detail;
      const errMsg = {
        id: `err-${Date.now()}`,
        role: "assistant",
        content:
          e.response?.status === 429
            ? "Too many messages — please wait a moment."
            : typeof detail === "string"
            ? detail
            : "Something went wrong. Please try again.",
      };
      setMessages((prev) => [...prev, errMsg]);
    } finally {
      setLoading(false);
      inputRef.current?.focus();
    }
  }, [input, loading, messages, apiHistory, sessionId, token]);

  const handleKey = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const clearChat = () => {
    localStorage.removeItem(getSessionKey(token));
    const sid = newSessionId();
    setSessionId(sid);
    const welcome = {
      id: "welcome",
      role: "assistant",
      content: `Hi again! How can I help you today?`,
      isWelcome: true,
    };
    setMessages([welcome]);
    setApiHistory([]);
    saveSession(token, { sessionId: sid, messages: [welcome], apiHistory: [], createdAt: Date.now() });
  };

  // ── Render states ──────────────────────────────────────────────────────────
  if (ctxLoading) {
    return (
      <div className="h-screen bg-[#F9F8F6] flex items-center justify-center">
        <div className="flex gap-1">
          {[0, 1, 2].map((i) => (
            <span
              key={i}
              className="w-2 h-2 rounded-full bg-[#9DB5A6] animate-bounce"
              style={{ animationDelay: `${i * 0.15}s` }}
            />
          ))}
        </div>
      </div>
    );
  }

  if (pageError) {
    return (
      <div className="h-screen bg-[#F9F8F6] flex items-center justify-center p-6">
        <div className="text-center" data-testid="widget-error">
          <div className="w-12 h-12 rounded-full bg-[#E8E3DC] flex items-center justify-center mx-auto mb-4 text-2xl">
            ✦
          </div>
          <p className="text-[#5C635A] text-sm">{pageError}</p>
        </div>
      </div>
    );
  }

  // Phase 10H — accent colour drives header, bubbles, send button, focus ring.
  // CSS variable approach: one root-level inline style; child elements use
  // `var(--widget-accent, #1A3626)` so the fallback is preserved if blank.
  const accent = ctx?.widget_accent_color || "#1A3626";

  return (
    <div
      className="flex flex-col bg-[#F9F8F6] overflow-hidden"
      style={{ height: "100dvh", "--widget-accent": accent }}
      data-testid="widget-page"
    >
      {/* Header */}
      <header
        className="flex items-center justify-between px-4 py-3 flex-shrink-0"
        style={{ backgroundColor: "var(--widget-accent, #1A3626)" }}
        data-testid="widget-header"
      >
        <div className="flex items-center gap-2.5 min-w-0">
          <div className="w-8 h-8 rounded-full bg-white/15 flex items-center justify-center flex-shrink-0 text-white font-semibold text-sm select-none">
            S
          </div>
          <div className="min-w-0">
            <div className="text-white font-semibold text-sm leading-tight truncate">Sophie</div>
            <div className="text-white/60 text-[10px] truncate">{ctx?.salon_name}</div>
          </div>
        </div>
        <div className="flex items-center gap-1">
          <div className="w-2 h-2 rounded-full bg-[#6FCF97] flex-shrink-0" title="Online" />
          <button
            onClick={clearChat}
            className="ml-2 text-white/50 hover:text-white/80 text-xs transition-colors"
            title="Start new chat"
            data-testid="widget-clear-btn"
          >
            New chat
          </button>
        </div>
      </header>

      {/* Messages */}
      <div
        className="flex-1 overflow-y-auto py-4"
        data-testid="widget-messages"
        style={{ scrollBehavior: "smooth" }}
      >
        {messages.map((m) => (
          <Bubble key={m.id || `${m.role}-${m.content?.slice(0, 20)}`} role={m.role} content={m.content} />
        ))}
        {loading && (
          <div className="flex justify-start px-3 mb-3">
            <div
              className="w-7 h-7 rounded-full flex-shrink-0 flex items-center justify-center text-white text-xs font-semibold mr-2 mt-1 select-none"
              style={{ backgroundColor: "var(--widget-accent, #1A3626)" }}
              data-testid="widget-loading-avatar"
            >
              S
            </div>
            <div className="bg-white border border-[#E8E3DC] rounded-2xl rounded-bl-md shadow-sm">
              <TypingDots />
            </div>
          </div>
        )}
        <div ref={bottomRef} />
      </div>

      {/* Input */}
      <div
        className="flex-shrink-0 px-3 pb-4 pt-2 bg-[#F9F8F6] border-t border-[#E8E3DC]"
        data-testid="widget-input-area"
      >
        <div
          className="flex items-end gap-2 bg-white border rounded-2xl px-3 py-2 transition-colors"
          style={{
            borderColor: inputFocused ? "var(--widget-accent, #1A3626)" : "#E0DBD0",
          }}
          data-testid="widget-input-wrapper"
        >
          <textarea
            ref={inputRef}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKey}
            onFocus={() => setInputFocused(true)}
            onBlur={() => setInputFocused(false)}
            placeholder="Message Sophie…"
            rows={1}
            className="flex-1 resize-none bg-transparent text-sm text-[#1C201E] placeholder-[#9DB5A6] outline-none min-h-[24px] max-h-[96px] leading-6"
            style={{ height: "auto", overflowY: "hidden" }}
            onInput={(e) => {
              e.target.style.height = "auto";
              e.target.style.height = Math.min(e.target.scrollHeight, 96) + "px";
            }}
            data-testid="widget-input"
            disabled={loading}
          />
          <button
            onClick={sendMessage}
            disabled={!input.trim() || loading}
            className="flex-shrink-0 w-8 h-8 rounded-xl disabled:bg-[#C0BCBA] flex items-center justify-center transition-colors"
            style={{
              backgroundColor:
                !input.trim() || loading ? "#C0BCBA" : "var(--widget-accent, #1A3626)",
            }}
            data-testid="widget-send-btn"
            aria-label="Send message"
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
              <path d="M5 12h14M12 5l7 7-7 7" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </button>
        </div>
        <p className="text-center text-[10px] text-[#C0BCBA] mt-1.5">
          Powered by Sophie · {ctx?.salon_name}
        </p>
      </div>
    </div>
  );
}
